import { useEffect, useRef, useState } from 'react';
import { api, darf, type Anmelder } from '../api.ts';
import { Fehlerkasten, ausFehler, type Fehlerlage } from '../teile/Formular.tsx';

// ============================================================ Mailvorlagen
//
// ORIGINALTEXT MARVIN (27.08.2026)
//   „gestalte es innovativer, weil wir haben ja auch unsere ganzen email
//    textvorlagen. da sind so nicht sauber dargstellt. an der seite müssen die
//    verfügbaren platzhalter auch immer stehen mit beispielen. das ist
//    notwendig."
//
// „AN DER SEITE" IST WÖRTLICH GEMEINT, und es ist der Kern dieses Bildschirms.
// Wer einen Brief schreibt, in den Werte eingesetzt werden, hat genau EINE
// wiederkehrende Frage: wie heisst der Platzhalter für das, was ich gerade
// meine? Solange die Antwort in einem anderen Fenster steht, schreibt man
// `{Vorname}`, weil es plausibel klingt - und merkt es beim ersten Kunden.
//
// „MIT BEISPIELEN" IST DER ZWEITE TEIL, und er ist der wichtigere. Eine Liste
// aus `{datum}`, `{uhrzeit}`, `{kls}` beantwortet die Frage nicht, die man
// wirklich hat: kommt da „14.09.2026" oder „2026-09-14"? Steht bei
// `{adresse}` schon die Postleitzahl, oder muss ich `{plz} {ort}`
// dazuschreiben und habe sie dann zweimal?
//
// DIE VORSCHAU BENUTZT DIESELBEN BEISPIELWERTE. So ist das, was danebensteht,
// mit dem, was herauskommt, dieselbe Wahrheit - und ein Tippfehler in einem
// Platzhalter fällt hier auf und nicht beim Kunden.
//
// EIN KLICK SETZT DEN PLATZHALTER AN DIE STELLE DES SCHREIBZEIGERS. Abtippen
// ist die Fehlerquelle, die diese Leiste beseitigen soll; wer ihn abtippen
// muss, tippt sich irgendwann in `{Kunde }` mit einem Leerzeichen.

/**
 * Ein Textfeld, in das sich Platzhalter an der Zeigerstelle einsetzen lassen.
 *
 * DIE STELLE WIRD GEMERKT UND NICHT GERATEN. Wer auf einen Platzhalter klickt,
 * hat damit den Fokus aus dem Textfeld genommen; ohne gemerkte Stelle landete
 * jeder Platzhalter am Ende des Textes, und das ist fast nie die Stelle, an
 * der man gerade war.
 */
function useEinsetzbar(wert: string, setze: (w: string) => void) {
  const feld = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);
  const stelle = useRef<number | null>(null);

  const merke = () => {
    const f = feld.current;
    if (f) stelle.current = f.selectionStart ?? null;
  };

  const einsetzen = (name: string) => {
    const marke = `{${name}}`;
    const bei = stelle.current ?? wert.length;
    const neu = wert.slice(0, bei) + marke + wert.slice(bei);
    setze(neu);
    stelle.current = bei + marke.length;
    // Der Zeiger springt hinter das Eingesetzte, damit man weiterschreiben
    // kann, ohne noch einmal zu klicken.
    queueMicrotask(() => {
      const f = feld.current;
      if (!f) return;
      f.focus();
      f.setSelectionRange(bei + marke.length, bei + marke.length);
    });
  };

  return { feld, merke, einsetzen };
}


export function Mailvorlagen({ ich }: { ich: Anmelder }) {
  const [d, setD] = useState<any>(null);
  const [wahl, setWahl] = useState<string>('erst');
  const [betreff, setBetreff] = useState('');
  const [text, setText] = useState('');
  const [schau, setSchau] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const pflegen = darf(ich, 'vorlage.pflegen');

  const laden = () => api.mailvorlagen().then((a) => {
    setD(a);
    const v = (a.vorlagen ?? []).find((x: any) => x.wert === wahl)
           ?? a.vorlagen?.[0];
    if (v) {
      setWahl(v.wert);
      setBetreff(v.betreff ?? '');
      setText(v.text ?? '');
    }
  }).catch((f) => setFehler(ausFehler(f)));

  /* UND HIER AUCH NICHT: `laden` setzt Betreff und Text der gewählten
     Vorlage. Wer eine Mailvorlage umschreibt, bekäme sie beim ersten
     Schreibvorgang irgendwo im Haus wieder auf den alten Stand gesetzt. */
  useEffect(() => { laden(); }, []);

  const vorlagen: any[] = d?.vorlagen ?? [];
  const v = vorlagen.find((x) => x.wert === wahl) ?? null;

  const feldBetreff = useEinsetzbar(betreff, setBetreff);
  const feldText = useEinsetzbar(text, setText);
  /* IN WELCHES FELD DER PLATZHALTER GEHT. Ohne diese Frage landete er immer
     im Textfeld - und ein `{KW}` in der Betreffzeile ist genau der Fall, in
     dem man ihn braucht. Gemerkt wird das zuletzt berührte Feld. */
  const [ziel, setZiel] = useState<'betreff' | 'text'>('text');

  function waehle(w: string) {
    const n = vorlagen.find((x) => x.wert === w);
    setWahl(w);
    setBetreff(n?.betreff ?? '');
    setText(n?.text ?? '');
    setSchau(null); setMeldung(null); setFehler(null);
  }

  const geaendert = !!v
    && ((v.betreff ?? '') !== betreff || (v.text ?? '') !== text);

  async function speichern() {
    if (!v) return;
    setLaeuft(true); setFehler(null); setMeldung(null);
    try {
      const a = await api.mailvorlageSpeichern(v.wert, { betreff, text });
      setMeldung(a.geaendert
        ? `„${v.name}" gespeichert.`
        : `An „${v.name}" hat sich nichts geändert.`);
      await laden();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  async function vorschau() {
    if (!v) return;
    setLaeuft(true); setFehler(null);
    try { setSchau(await api.mailvorschau(v.wert, { betreff, text })); }
    catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  /* DIE VORLAGEN STEHEN IN GRUPPEN, und die Gruppe sagt, WER die Mail
     bekommt. „An den Kunden" und „An die Telekom" sind zwei verschiedene
     Sorgfaltspflichten; sie in einer Liste zu führen wäre die Sorte Ordnung,
     die keine ist. */
  const gruppen = [...new Set(vorlagen.map((x) => x.gruppe))];

  return (
    <>
      <div className="kopf">
        <h1>Mailvorlagen</h1>
        <div className="wachsen" />
        <span className="klein leise">
          {d === null ? 'Wird geladen.' : `${vorlagen.length} Vorlagen`}
        </span>
        {pflegen && (
          <div className="reihe">
            <button className="knopf" disabled={laeuft} onClick={vorschau}>
              Vorschau
            </button>
            <button className="knopf haupt" disabled={laeuft || !geaendert}
                    onClick={speichern}>
              {laeuft ? 'Einen Moment' : 'Speichern'}
            </button>
          </div>
        )}
      </div>

      <div className="hinweis klein" style={{ marginBottom: 12 }}>
        <b>Hier stehen alle Texte, die als Mail hinausgehen.</b> Die
        Platzhalter in geschweiften Klammern werden beim Versenden ersetzt,
        groß wie klein geschrieben: <code>{'{Kunde}'}</code> und
        {' '}<code>{'{kunde}'}</code> treffen denselben Wert. Was keinen
        Wert hat, bleibt leer und wird gemeldet - ein
        {' '}<code>{'{kunde}'}</code> in der versendeten Mail ist peinlicher
        als eine leere Stelle.
        {d?.hinweis && (
          <div style={{ fontWeight: 400, marginTop: 4 }}>{d.hinweis}</div>
        )}
      </div>

      <Fehlerkasten lage={fehler} />
      {meldung && <div className="hinweis">{meldung}</div>}

      <div className="vorlagenraster">
        {/* LINKS: WELCHE VORLAGE. */}
        <div className="vorlagenwahl">
          {gruppen.map((g) => (
            <div key={g}>
              <h4>{g}</h4>
              {vorlagen.filter((x) => x.gruppe === g).map((x) => (
                <button key={x.wert} type="button"
                        className={'vorlagenknopf'
                                 + (x.wert === wahl ? ' gewaehlt' : '')}
                        onClick={() => waehle(x.wert)}>
                  <b>{x.name}</b>
                  <span className="klein leise">
                    {x.text ? `${x.text.length} Zeichen`
                            : x.faelltZurueckAuf
                              ? `leer, gilt „${x.faelltZurueckAuf}"`
                              : 'noch kein Text'}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* MITTE: DER TEXT. */}
        <div className="vorlagenfeld">
          {!v && <p className="klein leise">Keine Vorlage gewählt.</p>}
          {v && (
            <>
              <h3 style={{ margin: '0 0 4px' }}>{v.name}</h3>
              <p className="klein leise" style={{ marginTop: 0 }}>
                {v.zweck} <b>Wann:</b> {v.wann}
              </p>

              {/* WAS PASSIERT, WENN DIESER TEXT LEER BLEIBT. „Leer heisst:
                  wie die Erstmail" ist besser als eine leere Mail - aber nur,
                  wenn man es weiss, und deshalb steht es hier. */}
              {v.faelltZurueckAuf && !v.text && (
                <div className="hinweis klein warnung">
                  Hier steht kein Text. Solange das so bleibt, geht der Text
                  von „{v.faelltZurueckAuf}" hinaus.
                </div>
              )}

              {v.betreffSchluessel && (
                <div className="feld">
                  <label htmlFor="mv-betreff">Betreff</label>
                  <input id="mv-betreff" value={betreff} disabled={!pflegen}
                         ref={feldBetreff.feld as any}
                         onFocus={() => setZiel('betreff')}
                         onSelect={feldBetreff.merke}
                         onKeyUp={feldBetreff.merke}
                         onClick={feldBetreff.merke}
                         onChange={(e) => setBetreff(e.target.value)}
                         style={{ width: '100%' }} />
                </div>
              )}

              <div className="feld">
                <label htmlFor="mv-text">Text</label>
                {/* EIN BRIEF BRAUCHT EIN BRIEFFELD. Diese Texte standen
                    bisher in einzeiligen Eingabefeldern der Konfiguration -
                    sechs Zeilen Brief in einer Zeile Fenster, in dem man
                    nach links und rechts wandert, um einen Satz zu lesen. */}
                <textarea id="mv-text" value={text} rows={16}
                          disabled={!pflegen}
                          ref={feldText.feld as any}
                          onFocus={() => setZiel('text')}
                          onSelect={feldText.merke}
                          onKeyUp={feldText.merke}
                          onClick={feldText.merke}
                          onChange={(e) => setText(e.target.value)}
                          style={{ width: '100%', fontFamily: 'inherit' }} />
                <div className="feldhinweis">
                  Der Schlüssel dieses Textes:{' '}
                  <code>{v.textSchluessel}</code>
                </div>
              </div>

              {schau && (
                <div className="karte" style={{ marginTop: 12 }}>
                  <div className="reihe kopfzeile">
                    <b className="wachsen">Vorschau mit Beispielwerten</b>
                  </div>
                  {schau.betreff && (
                    <p style={{ margin: '6px 0' }}>
                      <b>Betreff:</b> {schau.betreff}
                    </p>
                  )}
                  <pre className="vorschautext">{schau.text}</pre>
                  {/* EIN PLATZHALTER, DEN ES NICHT GIBT, IST DER EIGENTLICHE
                      ERTRAG DIESER VORSCHAU. Er bleibt im Text stehen und
                      ginge sonst genau so zum Kunden. */}
                  {!!schau.unbekannt?.length && (
                    <div className="hinweis klein fehler">
                      <b>Diese Platzhalter gibt es nicht:</b>{' '}
                      {schau.unbekannt.map((u: string) => `{${u}}`).join(', ')}.
                      Sie bleiben so stehen, wie sie dastehen - auch in der
                      Mail, die beim Empfänger ankommt.
                    </div>
                  )}
                  {!!schau.leer?.length && (
                    <div className="hinweis klein warnung">
                      <b>Ohne Wert und deshalb leer:</b>{' '}
                      {schau.leer.map((u: string) => `{${u}}`).join(', ')}.
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* RECHTS: DIE PLATZHALTER. „an der seite müssen die verfügbaren
            platzhalter auch immer stehen mit beispielen." */}
        <div className="platzhalterleiste">
          <h4>Platzhalter</h4>
          <p className="klein leise" style={{ marginTop: 0 }}>
            Anklicken setzt ihn an die Stelle des Schreibzeigers
            {' '}({ziel === 'betreff' ? 'Betreff' : 'Text'}).
          </p>
          {(v?.platzhalter ?? []).map((p: any) => (
            <button key={p.feld} type="button" className="platzhalter"
                    disabled={!pflegen}
                    onClick={() => (ziel === 'betreff'
                      ? feldBetreff.einsetzen(p.feld)
                      : feldText.einsetzen(p.feld))}>
              <code>{'{' + p.feld + '}'}</code>
              {/* DAS BEISPIEL IST NICHT DIE ZIERDE, SONDERN DER PUNKT. */}
              <span className="beispiel">{p.beispiel}</span>
              <span className="klein leise">{p.zweck}</span>
            </button>
          ))}
          {!v?.platzhalter?.length && (
            <p className="klein leise">
              Diese Vorlage kennt keine Platzhalter.
            </p>
          )}
        </div>
      </div>
    </>
  );
}
